import { DndSortableHeadersContext } from "@/components/tables/dnd.sortable.headers.context";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { TableIds, TableSettings, useTableStore } from "@/store/tables";
import { horizontalListSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { ColumnDef, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { TableTh } from "../../../../custom-ui/tables/table.th";
import { Button } from "@/components/ui/button";
import { Pin, PinOff } from "lucide-react";
import { TableTd } from "../../../../custom-ui/tables/table.td";

interface ConversionCardTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  tableId: TableIds;
  savedSettings: TableSettings;
  isSidebarVisible: boolean;
}
export const ConversionCardTable = <T,>({
  data,
  columns,
  tableId,
  savedSettings,
  isSidebarVisible,
}: ConversionCardTableProps<T>) => {
  const { setTableSettings } = useTableStore();
  const columnOrder =
    savedSettings.columnOrder.length > 0 ? savedSettings.columnOrder : columns.map((c) => c.id!);

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    onColumnOrderChange: (updater) => {
      setTableSettings(tableId, {
        ...savedSettings,
        columnOrder: updater instanceof Function ? updater(savedSettings.columnOrder) : updater,
      });
    },
    state: {
      columnOrder,
    },
  });

  return (
    <DndSortableHeadersContext table={table} tableId={tableId}>
      <ScrollArea
        type="hover"
        className={`relative mb-2 border rounded-md  max-h-[calc(100dvh-180px)] ${isSidebarVisible ? "max-w-[calc(100vw-100px)]" : "max-w-[calc(100vw-280px)]"} transition-all duration-300 ease-linear`}
      >
        <table className="min-w-full border-collapse">
          <thead className="sticky top-0 bg-slate-500 h-12">
            {table.getHeaderGroups().map((headersGroup) => (
              <tr key={headersGroup.id} className="border-b">
                <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                  {headersGroup.headers.map((header) => {
                    return (
                      <TableTh header={header} key={header.id}>
                        <>
                          {header.column.getCanPin() && (
                            <div className="flex gap-1 justify-center">
                              {header.column.getIsPinned() !== "left" ? (
                                <Button
                                  className="px-1"
                                  variant={"ghost"}
                                  onClick={() => {
                                    header.column.pin("left");
                                  }}
                                >
                                  <Pin size={"16"} />
                                </Button>
                              ) : null}
                              {header.column.getIsPinned() ? (
                                <Button
                                  variant={"ghost"}
                                  className="text-primary px-1"
                                  onClick={() => {
                                    header.column.pin(false);
                                  }}
                                >
                                  <PinOff size={"16"} />
                                </Button>
                              ) : null}
                            </div>
                          )}
                          {header.column.getCanResize() && (
                            <div
                              onDoubleClick={() => header.column.resetSize()}
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              className={`absolute right-0 top-1/2 h-[30%] w-0.5 cursor-col-resize touch-none select-none transform -translate-y-1/2 ${
                                header.column.getIsResizing()
                                  ? "bg-primary opacity-100"
                                  : "bg-muted"
                              }`}
                            />
                          )}
                        </>
                      </TableTh>
                    );
                  })}
                </SortableContext>
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="cursor-pointer border-b transition-colors hover:bg-accent/50 h-20"
              >
                {row.getVisibleCells().map((cell) => {
                  return (
                    <SortableContext
                      key={cell.id}
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      <TableTd cell={cell} />
                    </SortableContext>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <ScrollBar orientation="vertical" />
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </DndSortableHeadersContext>
  );
};
